import { useCallback, useEffect, useState } from 'react';

/**
 * Per-classroom "worked-through" set persisted in sessionStorage — the items a
 * teacher has already played/opened during a live session, so finished items
 * move out of the way until Reset. Cleared when the tab closes (each day starts
 * fresh). Shared by the YouTube playlist (played videos) and Links (opened
 * links). `kind` namespaces the storage key (e.g. 'youtubePlayed', 'linksOpened').
 */
export function useWorkedThrough(classroomId: string | null | undefined, kind: string) {
  const storageKey = classroomId ? `classy:${kind}:${classroomId}` : null;
  const [done, setDone] = useState<Set<string>>(() => read(storageKey));

  useEffect(() => {
    setDone(read(storageKey));
  }, [storageKey]);

  const persist = useCallback(
    (next: Set<string>) => {
      setDone(next);
      write(storageKey, next);
    },
    [storageKey],
  );

  const markDone = useCallback(
    (id: string) => {
      setDone((prev) => {
        const next = new Set(prev);
        next.add(id);
        write(storageKey, next);
        return next;
      });
    },
    [storageKey],
  );

  const reset = useCallback(() => persist(new Set()), [persist]);

  /** Drop ids no longer present (e.g. a removed item) so the set stays tidy. */
  const prune = useCallback(
    (validIds: Iterable<string>) => {
      const valid = new Set(validIds);
      setDone((prev) => {
        const next = new Set([...prev].filter((id) => valid.has(id)));
        if (next.size !== prev.size) write(storageKey, next);
        return next;
      });
    },
    [storageKey],
  );

  return { done, markDone, reset, prune };
}

function read(key: string | null): Set<string> {
  if (!key) return new Set();
  try {
    const raw = sessionStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

function write(key: string | null, ids: Set<string>): void {
  if (!key) return;
  try {
    sessionStorage.setItem(key, JSON.stringify([...ids]));
  } catch {
    /* ignore quota / private mode */
  }
}
